import React, { memo, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTableSortEvent } from 'primereact/datatable'

import {
    ChargePointUser,
    useCompanyGetUsersQuery,
    useCompanyRemoveUserMutation,
} from '../../../common/redux/api/web-api'
import { filterFieldsBySearchValue } from '../../../lib/filter-fields-by-search-value'
import { PageLayoutContainer } from '../../../lib/page-layout-container'
import hasClaimForCompany from '../../../lib/hasClaimForCompany'
import { WidgetLayout } from '../../../lib/widget-layout'
import { RootState } from '../../redux/store'
import Claims from '../../../lib/claims'
import useText from '../../../common/hooks/useText'
import { confirm } from '../../../common/uikit/ConfirmDialog'
import { getErrorMessageCode } from '../../../common/utils/getErrorMessageCode'
import { Input } from '../../../common/uikit/Input'
import { Table } from '../../../common/uikit/Table'
import { useToast } from '../../../common/uikit/ToastProvider'
import { setSelectedUser } from '../../redux/features/selectedUser/selectedUserSlice'

import { CreateUserDialog } from './dialogs/create-user-dialog'

enum SortParameter {
    FIRSTNAME = 'firstName',
    EMAIL = 'email',
    MOBILE = 'mobile',
}

export const UsersPage = memo(function UsersPage(): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const dispatch = useDispatch()
    const [createUserDialogVisible, setCreateUserDialogVisible] = useState(false)
    const [searchValue, setSearchValue] = useState('')
    const [sortFilter, setSortFilter] = useState<{ parameter: SortParameter; order: 1 | -1 }>({
        parameter: SortParameter.EMAIL,
        order: 1,
    })

    const user = useSelector((state: RootState) => state.user)
    const isAdmin = hasClaimForCompany(user, user.selectedCompanyId, Claims.Admin)
    const {
        data: users,
        isFetching: loading,
        refetch,
    } = useCompanyGetUsersQuery(
        { companyId: user.selectedCompanyId },
        { skip: !user.selectedCompanyId, refetchOnMountOrArgChange: true }
    )
    const [removeUser] = useCompanyRemoveUserMutation()

    const filteredUsers = useMemo(
        () =>
            (users ?? []).filter((listedUser) =>
                filterFieldsBySearchValue(
                    [
                        listedUser.firstName ?? '',
                        listedUser.mobile ?? '',
                        listedUser.lastName ?? '',
                        listedUser.email ?? '',
                        listedUser.userStatus,
                    ],
                    searchValue
                )
            ),
        [searchValue, users]
    )

    const removeUserValue = async (userId: string) => {
        const confirmed = await confirm({
            header: text('lblProceedConfirmation'),
            message: text('msgRemoveUser'),
            variant: 'danger',
        })
        if (!confirmed) return

        try {
            await removeUser({ userId, companyId: user.selectedCompanyId }).unwrap()
            toast.notifySuccess(text('msgUserRemoved'))
            await refetch()
        } catch (error: unknown) {
            const messageCode = getErrorMessageCode(error)

            if (messageCode === 'msgUserWithMDCantBeRemovedFromCompany') {
                toast.notifyError(text('msgUserWithMDCantBeRemovedFromCompany'))
                return
            }
            if (messageCode === 'msgUserWithTagsCantBeRemovedFromCompany') {
                toast.notifyError(text('msgUserWithTagsCantBeRemovedFromCompany'))
                return
            }

            toast.notifyError(text('msgUserNotRemoved'))
        }
    }

    const onSort = (event: DataTableSortEvent) => {
        setSortFilter({
            parameter: event.sortField as SortParameter,
            order: event.sortOrder === -1 ? -1 : 1,
        })
    }

    return (
        <PageLayoutContainer>
            <WidgetLayout
                title={text('lblUsers')}
                headerButtons={
                    isAdmin ? (
                        <Button
                            type="button"
                            label={text('btnCreate')}
                            icon="pi pi-plus"
                            onClick={() => setCreateUserDialogVisible(true)}
                            data-testid="create-user"
                        />
                    ) : null
                }
            >
                <div className="flex mb-3">
                    <Input
                        id="user-search"
                        type="search"
                        value={searchValue}
                        onChange={(event) => setSearchValue(event.target.value)}
                        placeholder={text('lblSearch')}
                        aria-label={text('lblSearch')}
                        icon="pi pi-search"
                        className="w-full md:w-20rem"
                        data-testid="search"
                    />
                </div>

                <Table
                    value={filteredUsers}
                    loading={loading}
                    sortField={sortFilter.parameter}
                    sortOrder={sortFilter.order}
                    onSort={onSort}
                    emptyMessage={searchValue ? text('msgNoResultsFound') : text('msgNoData')}
                    data-testid="users-table"
                >
                    <Column
                        field={SortParameter.FIRSTNAME}
                        header={text('lblName')}
                        sortable
                        body={(listedUser: ChargePointUser) => (
                            <Link to={`/user/${listedUser.id}`} onClick={() => dispatch(setSelectedUser(listedUser))}>
                                {listedUser.firstName} {listedUser.lastName}
                            </Link>
                        )}
                    />
                    <Column field={SortParameter.EMAIL} header={text('lblEmail')} sortable />
                    <Column field={SortParameter.MOBILE} header={text('lblMobile')} sortable />
                    <Column
                        field="userStatus"
                        header={text('lblStatus')}
                        body={(listedUser: ChargePointUser) =>
                            listedUser.userStatus === 'Valid' ? text('lblValidStatus') : text('lblInvalidStatus')
                        }
                    />
                    <Column field="roleInCompany" header={text('lblRole')} />
                    <Column
                        body={(listedUser: ChargePointUser) =>
                            isAdmin && listedUser.id ? (
                                <div className="flex justify-content-end gap-2">
                                    <Button
                                        type="button"
                                        icon="pi pi-times"
                                        size="small"
                                        outlined
                                        rounded
                                        severity="danger"
                                        aria-label={text('btnRemove')}
                                        tooltip={text('btnRemove')}
                                        tooltipOptions={{ position: 'top' }}
                                        onClick={() => void removeUserValue(listedUser.id!)}
                                    />
                                </div>
                            ) : null
                        }
                    />
                </Table>
            </WidgetLayout>
            <CreateUserDialog
                visible={createUserDialogVisible}
                onHide={() => setCreateUserDialogVisible(false)}
                onSuccess={async () => {
                    await refetch()
                    setCreateUserDialogVisible(false)
                }}
            />
        </PageLayoutContainer>
    )
})
